import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { api } from '../utils/api';
import toast from 'react-hot-toast';
import { updateUser as apiUpdateUser } from '../utils/api';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isApproved: boolean;
  isAdmin: boolean;
  hasTemporaryPassword: boolean;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (userData: {
	email: string;
	password: string;
	firstName: string;
	lastName: string;
	paymentType?: string;
	organizationEventName?: string
}) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  updateUser: (userData: Partial<User>) => Promise<User>;
  isImpersonating: boolean;
  impersonatedUser: User | null;
  originalAdminEmail: string | null;
  startImpersonation: (userId: number) => Promise<void>;
  stopImpersonation: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedUser, setImpersonatedUser] = useState<User | null>(null);
  const [originalAdminEmail, setOriginalAdminEmail] = useState<string | null>(null);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await api.get('/auth/me');
      const userData = response.data;
      setUser(userData);

      // Check for impersonation metadata
      setIsImpersonating(userData.is_impersonating || false);
      if (userData.is_impersonating) {
        setImpersonatedUser(userData);
        setOriginalAdminEmail(userData.original_admin_email);
      } else {
        setImpersonatedUser(null);
        setOriginalAdminEmail(null);
      }
    } catch (error) {
      localStorage.removeItem('authToken');
      setUser(null);
      setIsImpersonating(false);
      setImpersonatedUser(null);
      setOriginalAdminEmail(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.post('/auth/login', { email, password });
      
      if (response.data.access_token) {
        localStorage.setItem('authToken', response.data.access_token);
        
        // If user data is included in response, use it; otherwise fetch it
        if (response.data.user) {
          setUser(response.data.user);
        } else {
          // Fetch user data separately
          try {
            const userResponse = await api.get('/auth/me');
            setUser(userResponse.data);
          } catch (userError) {
            console.warn('Failed to fetch user data after login:', userError);
          }
        }
        
        toast.success('Welcome back!');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Login failed';
      setError(message);
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData: { email: string; password: string; firstName: string; lastName: string; paymentType?: string; organizationEventName?: string }) => {
    try {
      setLoading(true);
      const response = await api.post('/auth/register', {
        email: userData.email,
        password: userData.password,
        first_name: userData.firstName,
        last_name: userData.lastName,
        payment_type: userData.paymentType || null,
        organization_event_name: userData.organizationEventName || null,
      });
      
      if (response.data.access_token) {
        localStorage.setItem('authToken', response.data.access_token);
        
        // If user data is included in response, use it; otherwise fetch it
        if (response.data.user) {
          setUser(response.data.user);
        } else {
          // Fetch user data separately
          try {
            const userResponse = await api.get('/auth/me');
            setUser(userResponse.data);
          } catch (userError) {
            console.warn('Failed to fetch user data after registration:', userError);
          }
        }
        
        toast.success('Account created successfully!');
        return { success: true };
      } else {
        return { success: false, error: 'Invalid response from server' };
      }
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Registration failed';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    setUser(null);
    toast.success('Logged out successfully');
  };

  const updateUser = async (userData: Partial<User>): Promise<User> => {
    try {
      setLoading(true);
      setError(null);
      const updatedUser = await apiUpdateUser(userData); // Corrected to call the imported API function
      setUser(updatedUser);
      toast.success('Profile updated successfully!');
      return updatedUser;
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to update profile';
      setError(message);
      toast.error(message);
      throw error; // Re-throw to indicate failure
    } finally {
      setLoading(false);
    }
  };

  const startImpersonation = async (userId: number) => {
    try {
      // Store current admin token
      const adminToken = localStorage.getItem('authToken');
      localStorage.setItem('originalAdminToken', adminToken || '');

      // Call impersonation endpoint
      const response = await api.post(`/admin/impersonate/${userId}`);
      const { access_token, impersonated_user, original_admin_email } = response.data;

      // Set impersonation token
      localStorage.setItem('authToken', access_token);

      // Update state
      setIsImpersonating(true);
      setImpersonatedUser(impersonated_user);
      setOriginalAdminEmail(original_admin_email);
      setUser(impersonated_user);

      toast.success(`Now impersonating ${impersonated_user.first_name || impersonated_user.email}`);
    } catch (error) {
      console.error('Failed to start impersonation:', error);
      toast.error('Failed to impersonate user');
      throw error;
    }
  };

  const stopImpersonation = async () => {
    try {
      // Restore original admin token
      const adminToken = localStorage.getItem('originalAdminToken');
      if (adminToken) {
        localStorage.setItem('authToken', adminToken);
        localStorage.removeItem('originalAdminToken');
      }

      // Clear impersonation state
      setIsImpersonating(false);
      setImpersonatedUser(null);
      setOriginalAdminEmail(null);

      // Refresh auth state to restore admin user
      await checkAuth();

      toast.success('Exited impersonation mode');
    } catch (error) {
      console.error('Failed to stop impersonation:', error);
      toast.error('Failed to exit impersonation');
      throw error;
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const value = {
    user,
    isAuthenticated: !!user,
    isApproved: !!user?.is_approved,
    isAdmin: user?.role === 'admin',
    hasTemporaryPassword: !!user?.is_temporary_password,
    loading,
    error,
    login,
    register,
    logout,
    checkAuth,
    setUser,
    updateUser,
    isImpersonating,
    impersonatedUser,
    originalAdminEmail,
    startImpersonation,
    stopImpersonation,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 